import {
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import type { components } from '@joyful/openapi/types.gen'
import {
  buildApiQueryKey,
  useApiQuery,
  useApiQueryClient,
} from '../../utils/reactQuery'
import { useApiOrgSlug } from '../../utils/orgSlug'
import type {
  AgentChatPart,
  AgentChatUiMessage,
} from './AgentChatToolInvocationSchemas'

type ConversationMessagePartsView =
  components['schemas']['ConversationMessagePartsView']

export const AGENT_SESSIONS_PATH = '/{org_slug}/agent-sessions'
export const AGENT_SESSION_PATH = '/{org_slug}/agent-sessions/{conversation_id}'
export const AGENT_SESSION_MESSAGES_PATH =
  '/{org_slug}/agent-sessions/{conversation_id}/messages'

export const AGENT_SESSIONS_PAGE_SIZE = 100

export type AgentSessionConversation =
  components['schemas']['AgentSessionConversationRead']
export type AgentSessionDetail = components['schemas']['AgentSessionDetailRead']

type AgentConversationMessage =
  components['schemas']['QaConversationMessageRead']

export type AgentSessionMessagesQueryData = ReadonlyArray<AgentChatUiMessage>

export async function toAgentSessionMessagesQueryData(
  messages: ReadonlyArray<AgentConversationMessage> | undefined,
): Promise<AgentSessionMessagesQueryData> {
  const { toAgentChatUiMessages } = await import('./AgentChatMessages')
  return toAgentChatUiMessages(messages ?? [])
}

export function mergeAgentSessionWorkMessages({
  messages,
  workMessages,
}: {
  readonly messages: ReadonlyArray<AgentChatUiMessage>
  readonly workMessages: ReadonlyArray<AgentChatUiMessage> | undefined
}) {
  if (workMessages === undefined || workMessages.length === 0) {
    return [...messages]
  }

  const workMessagesById = new Map(
    workMessages.map((workMessage) => {
      return [workMessage.id, workMessage]
    }),
  )
  return messages.map((message) => {
    const hydratedMessage = workMessagesById.get(message.id)
    if (hydratedMessage === undefined) {
      return message
    }

    return {
      ...hydratedMessage,
      metadata: {
        ...message.metadata,
        ...hydratedMessage.metadata,
        createdAt:
          message.metadata?.createdAt ?? hydratedMessage.metadata?.createdAt,
        hasDeferredWork: false,
        user: message.metadata?.user ?? hydratedMessage.metadata?.user ?? null,
      },
      parts: mergeHydratedPartsWithLiveTail({
        hydratedParts: hydratedMessage.parts,
        liveParts: message.parts,
      }),
    }
  })
}

function mergeHydratedPartsWithLiveTail({
  hydratedParts,
  liveParts,
}: {
  readonly hydratedParts: AgentChatUiMessage['parts']
  readonly liveParts: AgentChatUiMessage['parts']
}) {
  const lastCheckpointIndex = liveParts.reduce((foundIndex, part, index) => {
    if (part.type === 'data-agent-stream-checkpoint') {
      return index
    }

    return foundIndex
  }, -1)
  if (lastCheckpointIndex < 0) {
    return hydratedParts
  }

  const hydratedPartKeys = hydratedParts.flatMap((part) => {
    const key = getAgentChatPartMergeKey(part)
    if (key === null) {
      return []
    }

    return [key]
  })
  const hydratedKeys = new Set(hydratedPartKeys)
  const liveTailParts = liveParts
    .slice(lastCheckpointIndex + 1)
    .filter((part) => {
      const key = getAgentChatPartMergeKey(part)
      if (key === null) {
        return true
      }

      return !hydratedKeys.has(key)
    })
  const mergedParts: AgentChatUiMessage['parts'] = []
  for (const part of hydratedParts) {
    mergedParts.push(part)
  }

  for (const part of liveTailParts) {
    mergedParts.push(part)
  }

  return mergedParts
}

function getAgentChatPartMergeKey(part: AgentChatPart) {
  if (part.type === 'data-agent-stream-checkpoint') {
    return part.type
  }

  if ('toolCallId' in part && typeof part.toolCallId === 'string') {
    return `${part.type}:${part.toolCallId}`
  }

  if ('id' in part && typeof part.id === 'string') {
    return `${part.type}:${part.id}`
  }

  return null
}

export type AgentSessionSourceType =
  | 'account'
  | 'case'
  | 'credential'
  | 'encounter'

interface UseAgentSessionsQueryParams {
  readonly conversation_ids?: ReadonlyArray<string>
  readonly enabled?: boolean
  readonly limit?: number
  readonly query?: string
  readonly refetchInterval?: number | false
  readonly source_id?: string
  readonly source_type?: AgentSessionSourceType
  readonly staleTime?: number
}

export function useAgentSessionsQuery({
  conversation_ids,
  limit,
  query,
  source_id,
  source_type,
  ...queryOptions
}: UseAgentSessionsQueryParams = {}) {
  const orgSlug = useApiOrgSlug()
  return useApiQuery(
    'get',
    AGENT_SESSIONS_PATH,
    {
      params: {
        path: {
          org_slug: orgSlug,
        },
        query: {
          conversation_ids: conversation_ids
            ? [...conversation_ids]
            : undefined,
          limit,
          query,
          source_id,
          source_type,
        },
      },
    },
    {
      placeholderData: conversation_ids ? undefined : keepPreviousData,
      ...queryOptions,
    },
  )
}

export type AgentSessionsQuery = ReturnType<typeof useAgentSessionsQuery>

export function useRecentEncounterAgentSessionsQuery(encounterId: string) {
  return useAgentSessionsQuery({
    limit: 10,
    refetchInterval: 5_000,
    source_id: encounterId,
    source_type: 'encounter',
    staleTime: 0,
  })
}

export function useAgentSessionQuery(conversationId: string) {
  const orgSlug = useApiOrgSlug()
  return useApiQuery(
    'get',
    AGENT_SESSION_PATH,
    {
      params: {
        path: {
          conversation_id: conversationId,
          org_slug: orgSlug,
        },
      },
    },
    {
      staleTime: 0,
    },
  )
}

export function flattenAgentSessionPages(
  data: InfiniteData<ReadonlyArray<AgentSessionConversation>> | undefined,
): Array<AgentSessionConversation> {
  return data?.pages.flat() ?? []
}

interface UseInfiniteAgentSessionsQueryParams extends Omit<
  UseAgentSessionsQueryParams,
  'refetchInterval'
> {
  readonly refetchInterval?:
    | number
    | false
    | ((sessions: ReadonlyArray<AgentSessionConversation>) => number | false)
}

export function useInfiniteAgentSessionsQuery({
  query,
  refetchInterval,
  source_id,
  source_type,
  staleTime,
}: UseInfiniteAgentSessionsQueryParams = {}) {
  const orgSlug = useApiOrgSlug()
  const client = useApiQueryClient()
  const sessionsQueryOptions = {
    params: {
      path: {
        org_slug: orgSlug,
      },
      query: {
        limit: AGENT_SESSIONS_PAGE_SIZE,
        query,
        source_id,
        source_type,
      },
    },
  }

  return useInfiniteQuery({
    getNextPageParam: (lastPage: ReadonlyArray<AgentSessionConversation>) => {
      if (lastPage.length < AGENT_SESSIONS_PAGE_SIZE) {
        return undefined
      }

      const lastConversation = lastPage.at(-1)
      if (
        lastConversation?.modified_at === undefined ||
        lastConversation.modified_at === null
      ) {
        return undefined
      }

      return {
        cursor: lastConversation.modified_at,
        cursor_id: lastConversation.id,
      }
    },
    // SAFETY: tanstack query infers the initial page param as undefined; pageParam is this cursor union.
    initialPageParam: undefined as
      | {
          readonly cursor: string
          readonly cursor_id: string
        }
      | undefined,
    queryFn: async ({ pageParam }) => {
      const response = await client.fetchClient.GET(AGENT_SESSIONS_PATH, {
        params: {
          path: {
            org_slug: orgSlug,
          },
          query: {
            cursor: pageParam?.cursor,
            cursor_id: pageParam?.cursor_id,
            limit: AGENT_SESSIONS_PAGE_SIZE,
            query,
            source_id,
            source_type,
          },
        },
      })
      if (response.error) {
        throw new Error(`Failed to load agent sessions`, {
          cause: response.error,
        })
      }

      return response.data ?? []
    },
    queryKey: [
      ...buildApiQueryKey(AGENT_SESSIONS_PATH, sessionsQueryOptions),
      'infinite',
    ],
    refetchInterval:
      typeof refetchInterval === 'function'
        ? (sessionsQuery) => {
            return refetchInterval(
              flattenAgentSessionPages(sessionsQuery.state.data),
            )
          }
        : refetchInterval,
    staleTime,
  })
}

export type InfiniteAgentSessionsQuery = ReturnType<
  typeof useInfiniteAgentSessionsQuery
>

export function useAgentSessionMessagesQuery({
  conversationId,
  partsView = 'surface',
  refetchInterval,
  refreshKey,
}: {
  readonly conversationId: string | null
  readonly partsView?: ConversationMessagePartsView
  readonly refetchInterval?: number | false
  readonly refreshKey?: string | null
}) {
  const orgSlug = useApiOrgSlug()
  const client = useApiQueryClient()
  const messagesQueryOptions = {
    params: {
      path: {
        conversation_id: conversationId ?? '',
        org_slug: orgSlug,
      },
      query: {
        parts_view: partsView,
      } satisfies {
        parts_view: ConversationMessagePartsView
      },
    },
  }
  const baseQueryKey = buildApiQueryKey(
    AGENT_SESSION_MESSAGES_PATH,
    messagesQueryOptions,
  )
  const queryKey =
    refreshKey === undefined ? baseQueryKey : [...baseQueryKey, refreshKey]

  return useQuery<AgentSessionMessagesQueryData>({
    queryFn: conversationId
      ? async () => {
          const response = await client.fetchClient.GET(
            AGENT_SESSION_MESSAGES_PATH,
            messagesQueryOptions,
          )
          if (response.error) {
            throw new Error(`Failed to load conversation messages`)
          }

          return toAgentSessionMessagesQueryData(response.data.messages)
        }
      : skipToken,
    queryKey,
    refetchInterval,
    refetchOnMount: refreshKey === undefined ? true : 'always',
    staleTime: 0,
  })
}

export function useAgentSessionWorkMessagesQuery({
  conversationId,
  enabled = true,
  messageIds,
  refreshKey,
}: {
  readonly conversationId: string
  readonly enabled?: boolean
  readonly messageIds: ReadonlyArray<string>
  readonly refreshKey?: string | null
}) {
  const orgSlug = useApiOrgSlug()
  const client = useApiQueryClient()
  const messagesQueryOptions = {
    params: {
      path: {
        conversation_id: conversationId,
        org_slug: orgSlug,
      },
      query: {
        message_ids: [...messageIds],
        parts_view: 'full',
      } satisfies {
        message_ids: Array<string>
        parts_view: ConversationMessagePartsView
      },
    },
  }
  const baseQueryKey = buildApiQueryKey(
    AGENT_SESSION_MESSAGES_PATH,
    messagesQueryOptions,
  )
  const queryKey =
    refreshKey === null || refreshKey === undefined
      ? baseQueryKey
      : [...baseQueryKey, refreshKey]

  return useQuery<AgentSessionMessagesQueryData>({
    placeholderData: keepPreviousData,
    queryFn: enabled
      ? async () => {
          const response = await client.fetchClient.GET(
            AGENT_SESSION_MESSAGES_PATH,
            messagesQueryOptions,
          )
          if (response.error) {
            throw new Error(`Failed to load conversation work`)
          }

          return toAgentSessionMessagesQueryData(response.data.messages)
        }
      : skipToken,
    queryKey,
    staleTime: 0,
  })
}

export function getAgentSessionConversationEncounterId(
  conversation: AgentSessionConversation,
) {
  return conversation.encounter_id ?? conversation.agent_session.encounter_id
}

export function getAgentSessionQueryConversationIds({
  conversationIds,
  newConversationId,
}: {
  readonly conversationIds: ReadonlyArray<string>
  readonly newConversationId: string
}) {
  const persistedConversationIds = conversationIds.filter((conversationId) => {
    return conversationId !== newConversationId
  })
  return persistedConversationIds.length > 0
    ? persistedConversationIds
    : undefined
}
